import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, PhoneCall, MessageSquare, Radio, BrainCircuit, Palette, Plug,
  ShieldAlert, CheckCircle2, Clock, CircleDashed, Lock, Download, FileClock,
  DatabaseBackup, LogOut, KeyRound, ServerCog, XCircle, Trash2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { ReferenceManager } from "@/components/ReferenceManager";
import { MfaSettings } from "@/components/MfaSettings";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const INTEGRATIONS = [
  { icon: Building2, name: "Carrier Portals", desc: "Review saved portal access in the encrypted credential vault.", tag: "Check setup", to: "/vault", roles: ["admin", "ops_manager", "agent"] },
  { icon: PhoneCall, name: "Call Tracking", desc: "Provider setup, agency numbers, call logs and recordings.", tag: "Check setup", to: "/telephony", roles: ["admin", "ops_manager", "agent", "bpo_closer"] },
  { icon: MessageSquare, name: "Texting / SMS", desc: "Review SMS provider readiness in Appointments before messaging.", tag: "Check setup", to: "/appointments", roles: ["admin", "ops_manager", "agent"] },
  { icon: Radio, name: "Ringba", desc: "Call routing and attribution connector is not implemented.", tag: "Planned" },
  { icon: BrainCircuit, name: "InstaBrain", desc: "Review quote placement and current integration readiness.", tag: "Check setup", to: "/quotes", roles: ["admin", "ops_manager", "agent"] },
];
const READINESS = [
  { name: "Stronger Authentication", status: "done", desc: "Strong password rules, rate-limit/lockout, forced first-login change, email reset." },
  { name: "Password Reset Flow", status: "done", desc: "Self-service forgot/reset via tokenized link (1hr expiry)." },
  { name: "Role Permissions", status: "done", desc: "Role-based backend access and agency-scoped user management." },
  { name: "Audit Logs", status: "done", desc: "Activity history on records + admin audit export." },
  { name: "Data Export", status: "done", desc: "Scoped operational export and audit export available below." },
  { name: "Secure Cookies & CORS", status: "progress", desc: "HttpOnly always; Secure+explicit CORS in production mode." },
  { name: "Automated Backups", status: "planned", desc: "Scheduled DB backups & restore drills (manual export for now)." },
  { name: "Sensitive Data Policy", status: "policy", desc: "Use only the dedicated encrypted vaults for credentials and restricted data. Never paste secrets into ordinary notes or Co-Pilot." },
];
const STATUS_UI = {
  done: { icon: CheckCircle2, cls: "text-emerald-600", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200", label: "Implemented" },
  progress: { icon: Clock, cls: "text-amber-600", badge: "bg-amber-50 text-amber-700 ring-amber-200", label: "Env-Gated" },
  planned: { icon: CircleDashed, cls: "text-slate-400", badge: "bg-slate-100 text-slate-600 ring-slate-200", label: "Planned" },
  policy: { icon: ShieldAlert, cls: "text-red-600", badge: "bg-red-50 text-red-700 ring-red-200", label: "Policy" },
};

function downloadJson(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Settings() {
  const { user, logoutAll } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const [s, setS] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);

  const load = async () => {
    setSettingsError("");
    setSettingsLoading(true);
    try {
      const { data } = await api.get("/settings");
      if (!data || typeof data !== "object" || Array.isArray(data)) throw new Error("Invalid settings response");
      setS(data);
    } catch {
      setSettingsError("Unable to load settings. Configuration has not been verified.");
    } finally {
      setSettingsLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const togglePilot = async (v) => { await api.put("/settings", { pilot_mode: v }).catch(() => {}); load(); };
  const markBackup = async () => { await api.put("/settings", { mark_backup: true }); toast.success("Manual backup completion recorded"); load(); };
  const exportAll = async () => { const { data } = await api.get("/export/all"); downloadJson("foa-operational-export.json", data); toast.success("Operational export downloaded"); load(); };
  const exportAudit = async () => { const { data } = await api.get("/export/audit"); downloadJson("foa-audit-log.json", data); toast.success("Audit log downloaded"); load(); };
  const doLogoutAll = async () => { await logoutAll(); navigate("/login"); };
  const resetAgencyOperationalData = async () => {
    setResetBusy(true);
    try {
      const { data: preview } = await api.get("/data-management/reset-preview");
      const total = Number(preview?.total || 0);
      if (total === 0) {
        toast.info("Agency operational data is already clear.");
        return;
      }
      const phrase = preview?.confirmation_phrase || "RESET MY AGENCY DATA";
      const typed = window.prompt(
        `Clean start will permanently remove ${total} operational record(s) for this agency only.\n\n` +
        `Users, credentials, integrations, security/compliance records and agency settings are preserved.\n\n` +
        `Type exactly: ${phrase}`
      );
      if (typed === null) return;
      if (typed.trim() !== phrase) {
        toast.error("Confirmation did not match. Nothing was deleted.");
        return;
      }
      const { data } = await api.post("/data-management/reset-operational", { confirm: phrase });
      toast.success(`Clean start complete — ${data.total_removed || 0} operational record(s) cleared.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Agency reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  if (!s) return (
    <div>
      <PageHeader title="Settings" subtitle="Workspace, security, data protection and integrations." />
      {settingsError ? <div role="alert" className="rounded-lg border border-border bg-card p-5 text-sm">
        <p>{settingsError}</p>
        <Button variant="outline" className="mt-3" onClick={load}>Retry loading settings</Button>
      </div> : <p role="status" className="py-8 text-sm text-muted-foreground">Loading settings…</p>}
    </div>
  );

  const environmentKnown = typeof s.is_production === "boolean";
  const Check = ({ ok, children }) => (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
      <span className={ok ? "" : "text-red-600"}>{children}</span>
    </div>
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle="Workspace, security, data protection and integrations." />

      {settingsLoading && <p role="status" className="mb-4 text-sm text-muted-foreground">Refreshing settings…</p>}
      {settingsError && <div role="alert" className="mb-4 rounded-lg border border-border bg-card p-5 text-sm">
        <p>{settingsError} Showing last loaded values.</p>
        <Button variant="outline" className="mt-3" onClick={load}>Retry loading settings</Button>
      </div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Workspace */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4"><Palette className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Workspace</h3></div>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Company</dt><dd className="font-medium">Fortunate Operations</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Signed in as</dt><dd className="font-medium">{user.name}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Role</dt><dd className="font-medium">{ROLE_LABELS[user.role]}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Session timeout</dt><dd className="font-medium">{s?.session_timeout_minutes ?? "—"} min</dd></div>
          </dl>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/change-password")} data-testid="change-password-btn"><KeyRound className="mr-1.5 h-4 w-4" /> Change Password</Button>
            <Button variant="outline" size="sm" onClick={doLogoutAll} data-testid="logout-all-btn"><LogOut className="mr-1.5 h-4 w-4" /> Log out all devices</Button>
          </div>
        </div>

        <MfaSettings />

        {/* Production controls */}
        <div className="rounded-lg border border-border bg-card p-5" data-testid="production-controls">
          <div className="flex items-center gap-2 mb-4"><ServerCog className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Production Controls</h3></div>
          <dl className="space-y-2.5 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Environment</dt><dd className="font-medium uppercase">{s.app_env || "Unknown"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Debug docs</dt><dd className="font-medium">{!environmentKnown ? "Unknown" : s.is_production ? "Disabled" : "Enabled (dev)"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Allowed origin</dt><dd className="font-medium truncate max-w-[55%]">{!environmentKnown ? "Unknown" : s.is_production ? s.allowed_origin || "Not reported" : "All (dev)"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Secure cookies</dt><dd className="font-medium">{!environmentKnown ? "Unknown" : s.is_production ? "HttpOnly · Secure · SameSite" : "HttpOnly (Secure in prod)"}</dd></div>
          </dl>
          {isAdmin && (
            <div className="mt-4 flex items-center justify-between rounded-md border border-border px-3 py-2">
              <div><div className="text-sm font-medium">Pilot Mode</div><div className="text-[11px] text-muted-foreground">Show internal-testing warning banner.</div></div>
              <Switch checked={!!s?.pilot_mode} onCheckedChange={togglePilot} data-testid="pilot-toggle" />
            </div>
          )}
          <div className="mt-4 rounded-md bg-secondary p-3">
            <div className="text-xs font-semibold mb-2">Pre-launch checklist</div>
            <div className="space-y-1.5">
              {typeof s.demo_accounts_present === "number" ? <Check ok={s.demo_accounts_present === 0}>Demo accounts removed ({s.demo_accounts_present} present)</Check> : <p className="text-sm text-muted-foreground">Demo account check: Unknown</p>}
              {typeof s.demo_data_present === "boolean" ? <Check ok={!s.demo_data_present}>Demo data removed</Check> : <p className="text-sm text-muted-foreground">Demo data check: Unknown</p>}
              {environmentKnown ? <Check ok={s.is_production}>Running in production mode</Check> : <p className="text-sm text-muted-foreground">Production mode: Unknown</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Data protection (admin) */}
      {isAdmin && (
        <div className="mt-6 rounded-lg border border-border bg-card p-5" data-testid="data-protection">
          <div className="flex items-center gap-2 mb-4"><Lock className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Data Protection</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button variant="outline" onClick={exportAll} data-testid="export-all-btn"><Download className="mr-1.5 h-4 w-4" /> Operational Export</Button>
            <Button variant="outline" onClick={exportAudit} data-testid="export-audit-btn"><FileClock className="mr-1.5 h-4 w-4" /> Audit Log Export</Button>
            <Button variant="outline" onClick={markBackup} data-testid="mark-backup-btn"><DatabaseBackup className="mr-1.5 h-4 w-4" /> Record Completed Backup</Button>
            <Button variant="outline" onClick={doLogoutAll}><LogOut className="mr-1.5 h-4 w-4" /> Logout All Devices</Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Operational export includes the original operational collections, safe user metadata, references and saved views. It is not a complete database backup. Record Completed Backup saves your own completion report; it does not verify a backup or restore.</p>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-md bg-secondary px-3 py-2"><dt className="text-[11px] uppercase text-muted-foreground">Reported backup status</dt><dd className="font-medium">{s?.backup_status}</dd></div>
            <div className="rounded-md bg-secondary px-3 py-2"><dt className="text-[11px] uppercase text-muted-foreground">Backup completion recorded</dt><dd className="font-medium">{s?.last_backup_at ? fmtDate(s.last_backup_at) : "—"}</dd></div>
            <div className="rounded-md bg-secondary px-3 py-2"><dt className="text-[11px] uppercase text-muted-foreground">Last operational export</dt><dd className="font-medium">{s?.last_full_export_at ? fmtDate(s.last_full_export_at) : "—"}</dd></div>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">Operational records use soft-delete and can be restored from each module's "Deleted" view.</p>

          <div className="mt-5 flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50/60 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="agency-clean-start">
            <div>
              <div className="text-sm font-semibold text-red-800">Clean Start · Agency Operational Data</div>
              <p className="mt-0.5 max-w-3xl text-xs text-red-700">
                Permanently clears this agency's leads/clients, policies, follow-ups, commissions, appointments, import history and other operational activity. Users, credentials, integrations, settings, DNC/consent/compliance and security records are preserved.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={resetAgencyOperationalData}
              disabled={resetBusy}
              data-testid="reset-agency-data-btn"
              className="shrink-0"
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> {resetBusy ? "Checking…" : "Reset Agency Data"}
            </Button>
          </div>
        </div>
      )}

      {/* Integrations */}
      <div className="mt-6 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4"><Plug className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Integrations</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {INTEGRATIONS.map((it) => (
            <div key={it.name} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`integration-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="grid h-9 w-9 place-items-center rounded-md bg-secondary text-muted-foreground"><it.icon className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="text-sm font-medium">{it.name}</span><span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">{it.tag}</span></div>
                <p className="text-xs text-muted-foreground truncate">{it.desc}</p>
              </div>
              {it.to && it.roles.includes(user.role) && <Button size="sm" variant="outline" onClick={() => navigate(it.to)}>Open</Button>}
            </div>
          ))}
        </div>
      </div>

      {/* Reference data (admin) */}
      {isAdmin && <div className="mt-6"><ReferenceManager /></div>}

      {/* Production readiness */}
      <div className="mt-6 rounded-lg border border-border bg-card p-5" data-testid="production-readiness">
        <div className="flex items-center gap-2 mb-1"><ShieldAlert className="h-5 w-5 text-primary" /><h3 className="font-heading text-base font-semibold">Production-Readiness Checklist</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Implementation checklist. Confirm deployed configuration and acceptance evidence before launch.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {READINESS.map((r) => {
            const ui = STATUS_UI[r.status];
            return (
              <div key={r.name} className="flex items-start gap-3 rounded-md border border-border p-3">
                <ui.icon className={`mt-0.5 h-4 w-4 shrink-0 ${ui.cls}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="text-sm font-medium">{r.name}</span><span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${ui.badge}`}>{ui.label}</span></div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{r.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
